import { AttachMoney, Build, CompareArrows, Group, PhotoCamera, Receipt, SelectAll, ShoppingCart, Storefront, Widgets, WorkOutline } from '@mui/icons-material';
import CalculateIcon from '@mui/icons-material/Calculate';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { Box, Button, Divider, Drawer, ListSubheader, Tooltip } from '@mui/material';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import PremiumFeatureBadge from './PremiumFeatureBadge';
import { hasPremiumAccess, PREMIUM_FEATURE_TOOLTIP, readStoredPlanName } from '../helpers/subscription';

function pathMatches(pathname, path, matchPaths = []) {
    if (pathname === path) {
        return true;
    }
    if (matchPaths.some((prefix) => pathname.startsWith(prefix))) {
        return true;
    }
    return false;
}

function SidebarNavItem({ item, pathname, darkMode, onClose, hasPremium }) {
    const active = pathMatches(pathname, item.path, item.matchPaths);
    const activeColor = darkMode ? 'yellow' : 'blue';
    const isPremiumLocked = item.premiumOnly && !hasPremium;

    const label = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {item.label}
            {item.premiumOnly && <PremiumFeatureBadge />}
        </Box>
    );

    const button = (
        <Button
            color="inherit"
            component={isPremiumLocked ? 'div' : Link}
            to={isPremiumLocked ? undefined : item.path}
            disabled={isPremiumLocked}
            startIcon={React.cloneElement(item.icon, { fontSize: 'large' })}
            onClick={isPremiumLocked ? undefined : onClose}
            sx={{
                justifyContent: 'flex-start',
                width: '100%',
                textTransform: 'none',
                color: active ? activeColor : 'inherit',
                fontSize: '1.05rem',
                fontWeight: active ? 700 : 400,
                py: 1,
                opacity: isPremiumLocked ? 0.72 : 1,
                '&:hover': {
                    backgroundColor: isPremiumLocked
                        ? 'transparent'
                        : darkMode
                            ? 'rgba(255, 255, 0, 0.1)'
                            : 'rgba(0, 0, 255, 0.08)',
                },
            }}
        >
            {label}
        </Button>
    );

    if (!isPremiumLocked) {
        return button;
    }

    return (
        <Tooltip title={PREMIUM_FEATURE_TOOLTIP} arrow>
            <Box>{button}</Box>
        </Tooltip>
    );
}

const MENU_SECTIONS = [
    {
        id: 'gerencial',
        title: 'Gerencial',
        items: [
            { path: '/admin', label: 'Dashboard', icon: <DashboardIcon /> },
            { path: '/admin/calculator', label: 'Calculadora de preço', icon: <CalculateIcon /> },
        ],
    },
    {
        id: 'operacional',
        title: 'Operacional',
        items: [
            {
                path: '/admin/printers',
                label: 'Impressoras',
                icon: <SelectAll />,
                matchPaths: ['/admin/register-printer', '/admin/edit-printer'],
            },
            { path: '/admin/jobs', label: 'Impressões', icon: <WorkOutline />, matchPaths: ['/admin/register-job', '/admin/edit-job'] },
            {
                path: '/admin/printer-maintenances',
                label: 'Manutenções',
                icon: <Build />,
                matchPaths: ['/admin/register-printer-maintenance', '/admin/edit-printer-maintenance'],
            },
            { path: '/admin/materials', label: 'Filamentos', icon: <Widgets />, matchPaths: ['/admin/register-material', '/admin/edit-material'] },
            { path: '/admin/products', label: 'Produtos', icon: <Storefront />, matchPaths: ['/admin/register-product', '/admin/edit-product'] },
        ],
    },
    {
        id: 'financeiro',
        title: 'Financeiro',
        items: [
            { path: '/admin/quick-checkout', label: 'Quick Checkout', icon: <PhotoCamera />, premiumOnly: true },
            { path: '/admin/checkouts', label: 'Checkouts', icon: <ShoppingCart />, matchPaths: ['/admin/register-checkout', '/admin/register-batch-checkout', '/admin/edit-checkout'] },
            { path: '/admin/orders', label: 'Vendas', icon: <AttachMoney /> },
            { path: '/admin/transactions', label: 'Transações', icon: <Receipt />, matchPaths: ['/admin/create-transaction', '/admin/edit-transaction'], premiumOnly: true },
            { path: '/admin/settlement-import', label: 'Conciliação Bancária', icon: <CompareArrows />, premiumOnly: true },
        ],
    },
    {
        id: 'administrativo',
        title: 'Administrativo',
        items: [
            { path: '/admin/customers', label: 'Clientes', icon: <Group />, matchPaths: ['/admin/register-customer', '/admin/edit-customer'] },
        ],
    },
];

const Sidebar = ({ open, onClose, darkMode }) => {
    const location = useLocation();
    const hasPremium = hasPremiumAccess(readStoredPlanName());

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            variant="temporary"
            sx={{
                '& .MuiDrawer-paper': {
                    width: 280,
                    boxSizing: 'border-box',
                    marginTop: '64px',
                },
            }}
        >
            <Box sx={{ px: 1, pb: 2 }}>
                {MENU_SECTIONS.map((section, index) => (
                    <Box key={section.id}>
                        {index > 0 && <Divider sx={{ my: 1 }} />}
                        <ListSubheader
                            component="div"
                            sx={{
                                lineHeight: 2,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                letterSpacing: '0.08em',
                                bgcolor: 'transparent',
                                color: darkMode ? 'grey.400' : 'text.secondary',
                                px: 1.5,
                            }}
                        >
                            {section.title}
                        </ListSubheader>
                        {section.items.map((item) => (
                            <SidebarNavItem
                                key={item.path}
                                item={item}
                                pathname={location.pathname}
                                darkMode={darkMode}
                                onClose={onClose}
                                hasPremium={hasPremium}
                            />
                        ))}
                    </Box>
                ))}
            </Box>
        </Drawer>
    );
};

export default Sidebar;
